import { io, type Socket } from 'socket.io-client';
import { authClient } from '@/auth';

/**
 * Resolves the Socket.IO server URL.
 *
 * Order of precedence:
 * 1. `VITE_SOCKET_URL` — explicit override (e.g. when the realtime service
 *    runs on a different host/port than the REST API).
 * 2. `VITE_API_URL` — same host as the REST API. Default in production when
 *    realtime is co-deployed.
 * 3. `http://localhost:4000` — local dev fallback.
 */
function resolveSocketUrl(): string {
  const explicit = import.meta.env.VITE_SOCKET_URL;
  if (typeof explicit === 'string' && explicit.length > 0) return explicit;

  const apiUrl = import.meta.env.VITE_API_URL;
  if (typeof apiUrl === 'string' && apiUrl.length > 0) return apiUrl;

  return 'http://localhost:4000';
}

/**
 * Extracts the player JWT from the auth client, if any. Returns an empty
 * string when the user isn't authenticated — the realtime gateway tolerates
 * missing tokens (public rooms still work; user-targeted events just pass
 * through).
 */
function currentAuthToken(): string {
  try {
    const headers = authClient.getAuthHeaders() as Record<string, string | undefined> | undefined;
    const raw = headers?.Authorization ?? headers?.authorization ?? '';
    return raw.startsWith('Bearer ') ? raw.slice('Bearer '.length) : raw;
  } catch {
    return '';
  }
}

let socket: Socket | null = null;
/** Token sent on the most recent handshake — '' when it was anonymous. */
let lastHandshakeToken = '';

/**
 * Returns the singleton Socket.IO client connected to the `/realtime` namespace.
 *
 * The `auth` field is a function so socket.io re-evaluates the token on every
 * (re)connect — that gives us token refresh for free without manually closing
 * and re-opening the socket.
 */
export function getSocket(): Socket {
  if (socket) return socket;

  socket = io(`${resolveSocketUrl()}/realtime`, {
    transports: ['websocket'],
    autoConnect: true,
    reconnection: true,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 5_000,
    auth: (cb) => {
      lastHandshakeToken = currentAuthToken();
      cb({ token: lastHandshakeToken });
    },
  });

  socket.on('connect', () => {
    console.info('[socket] connected', socket?.id);
  });
  socket.on('disconnect', (reason) => {
    console.warn('[socket] disconnected:', reason);
  });
  socket.on('connect_error', (err) => {
    console.error('[socket] connect_error:', err.message);
  });

  return socket;
}

/**
 * Re-handshake when the auth *identity* changed (anonymous ↔ signed-in).
 *
 * The singleton connects with whatever token exists at that moment — a socket
 * opened while browsing as a guest handshakes with `token: ''` and then STAYS
 * on that anonymous identity after signup/login, because the `auth` callback
 * only re-runs on reconnect. This forces that reconnect so user-scoped rooms
 * (notifications, balance) start flowing. Presence-based on purpose: a routine
 * token rotation keeps the same identity and doesn't warrant dropping the
 * connection. No-op when the socket was never created.
 */
export function refreshSocketAuth(): void {
  if (!socket) return;
  const hasTokenNow = currentAuthToken() !== '';
  const handshookWithToken = lastHandshakeToken !== '';
  if (hasTokenNow === handshookWithToken) return;
  socket.disconnect();
  socket.connect();
}
